// Units of measure for the Estimate Engine catalog (db/81
// EstimateUnitsOfMeasure). Pure helpers — the UomSelect component and the
// admin tab read these; nothing here touches the network.
//
// DEFAULT_UOMS is the fallback the pickers use when the catalog has not
// loaded (or the call failed): exactly the four values the old hardcoded
// <select> offered, in its old order, so the form still works offline and a
// pre-catalog deploy renders identically.
export const DEFAULT_UOMS = [
  { Code: "ea", Name: "Each",        Kind: "count",  IsActive: true },
  { Code: "lf", Name: "Linear feet", Kind: "length", IsActive: true },
  { Code: "sf", Name: "Square feet", Kind: "area",   IsActive: true },
  { Code: "hr", Name: "Hours",       Kind: "time",   IsActive: true },
];

// Vocabulary for EstimateUnitsOfMeasure.Kind — mirrors the proc's CHECK list.
export const UOM_KINDS = [
  { key: "count",  label: "Count" },
  { key: "length", label: "Length" },
  { key: "area",   label: "Area" },
  { key: "volume", label: "Volume" },
  { key: "weight", label: "Weight" },
  { key: "time",   label: "Time" },
  { key: "flow",   label: "Flow" },
  { key: "lump",   label: "Lump sum" },
  { key: "other",  label: "Other" },
];

export const normalizeUomCode = (code) => String(code ?? "").trim().toLowerCase();

// The options a UOM picker should offer for a row whose stored value is
// `current`:
//   • every ACTIVE catalog unit, in catalog order (falls back to DEFAULT_UOMS
//     when the catalog is empty / not loaded);
//   • plus the row's current value when it is not among them (a retired unit,
//     or a legacy free-text value) — flagged `retired: true` and appended last,
//     so an existing row never shows a blank / wrong selection and editing an
//     unrelated field never silently rewrites its unit.
export function uomOptions(uoms, current) {
  const source = Array.isArray(uoms) && uoms.length ? uoms : DEFAULT_UOMS;
  const active = source.filter(u => u.IsActive !== false && u.IsActive !== 0);
  const seen = new Set();
  const out = [];
  for (const u of active) {
    const code = normalizeUomCode(u.Code);
    if (!code || seen.has(code)) continue;
    seen.add(code);
    out.push({ code, label: u.Name ? `${code} — ${u.Name}` : code, retired: false });
  }
  const cur = normalizeUomCode(current);
  if (cur && !seen.has(cur)) {
    const known = source.find(u => normalizeUomCode(u.Code) === cur);
    out.push({ code: cur, label: known?.Name ? `${cur} — ${known.Name} (retired)` : `${cur} (not in catalog)`, retired: true });
  }
  return out;
}

// Client-side twin of sp_SaveEstimateUnitOfMeasure's code rule, so the quick
// add can refuse "sq. ft." before a round trip. Returns null when valid, else
// the message to show.
export function validateUomCode(code) {
  const c = normalizeUomCode(code);
  if (!c) return "Enter a unit code (e.g. cfm).";
  if (c.length > 20) return "Unit code must be 20 characters or fewer.";
  if (/[^a-z0-9/-]/.test(c)) return 'Unit code may only contain letters, digits, "/" and "-".';
  return null;
}
